import { useState, useEffect } from "react";
import { InternalMediaCarouselItem } from "./InternalMediaCarouselItem";
import { Spinner } from "../../uiLibrary/spinner/Spinner";
import { Warning } from "../../uiLibrary/icons/Warning";
import { useTheme } from "@emotion/react";
import { useSingleton } from "@/other/injection";
import { MessageBusService } from "@/other/messageBus/MessageBusService";
import {
    IApiResult,
    useSubscription
} from "@/uiLibrary/virtualScroll/useSubscription";
import {
    getInternalMediaItemId,
    internalMediaSortCompare,
    INTERNAL_MEDIA_PAGE_SIZE,
    subscribeToInternalMedia
} from "./internalMediaQuery";

const MAX_ITEMS = INTERNAL_MEDIA_PAGE_SIZE;

export const InternalMediaCarousel = () => {
    return (
        <div className="flex flex-grow flex-col">
            <InternalMediaCarouselFeed />
        </div>
    );
};

function InternalMediaCarouselFeed() {
    const theme = useTheme();
    const isDark = theme.name === "dark";
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    const messageBusService = useSingleton(MessageBusService);

    const createSubscription = (
        callback: (
            response: IApiResult<
                RedDeer.InternalMedia.Client.UI.Contracts.Dtos.InternalMediaDto,
                string
            >
        ) => void
    ) => subscribeToInternalMedia(messageBusService, callback, MAX_ITEMS);
    const { items: subscribedItems, hasResult } = useSubscription({
        createSubscription,
        getItemId: getInternalMediaItemId,
        sortCompare: internalMediaSortCompare
    });

    const items = subscribedItems.slice(0, MAX_ITEMS);

    // Auto-rotate carousel every 5 seconds (pause on hover)
    useEffect(() => {
        if (items.length === 0 || isHovered) return;

        const interval = setInterval(() => {
            setCurrentIndex(prevIndex => (prevIndex + 1) % items.length);
        }, 5000);

        return () => {
            clearInterval(interval);
        };
    }, [items.length, isHovered]);

    if (!hasResult) {
        return <Loading />;
    }

    if (items.length === 0) {
        return <Error message="No posts available" />;
    }

    const currentItem = items[currentIndex];

    if (!currentItem) {
        return <Error message="No post found" />;
    }

    const goToPrevious = () => {
        setCurrentIndex(
            prevIndex => (prevIndex - 1 + items.length) % items.length
        );
    };

    const goToNext = () => {
        setCurrentIndex(prevIndex => (prevIndex + 1) % items.length);
    };

    return (
        <div
            className={`mb-12 rounded-2xl p-8 shadow-xl transition-all duration-500 ${
                isDark
                    ? "bg-gradient-to-br from-slate-800 to-slate-700"
                    : "bg-gradient-to-br from-white to-indigo-50"
            }`}
            style={{ aspectRatio: "21/9" }}
            onMouseEnter={() => {
                setIsHovered(true);
            }}
            onMouseLeave={() => {
                setIsHovered(false);
            }}
        >
            <div className="flex h-full flex-col justify-between">
                <InternalMediaCarouselItem item={currentItem} />

                <div className="mt-4 flex items-center justify-center gap-4">
                    {/* Left Arrow */}
                    {items.length > 1 && (
                        <button
                            onClick={goToPrevious}
                            className={`rounded-full p-2 transition-all duration-300 ${
                                isDark
                                    ? "text-slate-400 hover:bg-slate-600/50 hover:text-white"
                                    : "text-slate-400 hover:bg-slate-200/50 hover:text-slate-800"
                            } ${
                                isHovered
                                    ? "scale-100 opacity-100"
                                    : "scale-75 opacity-0"
                            }`}
                            aria-label="Previous slide"
                        >
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>
                    )}

                    {/* Dot Indicators */}
                    <div className="flex gap-2">
                        {items.map((item, index) => (
                            <button
                                key={item.id}
                                onClick={() => {
                                    setCurrentIndex(index);
                                }}
                                className={`h-2 rounded-full transition-all ${
                                    index === currentIndex
                                        ? "w-6 bg-indigo-600"
                                        : isDark
                                          ? "w-2 bg-slate-600 hover:bg-slate-500"
                                          : "w-2 bg-slate-300 hover:bg-slate-400"
                                }`}
                                title={item.title}
                                aria-label={`Go to slide ${String(index + 1)}: ${item.title ?? "Untitled"}`}
                            />
                        ))}
                    </div>

                    {/* Right Arrow */}
                    {items.length > 1 && (
                        <button
                            onClick={goToNext}
                            className={`rounded-full p-2 transition-all duration-300 ${
                                isDark
                                    ? "text-slate-400 hover:bg-slate-600/50 hover:text-white"
                                    : "text-slate-400 hover:bg-slate-200/50 hover:text-slate-800"
                            } ${
                                isHovered
                                    ? "scale-100 opacity-100"
                                    : "scale-75 opacity-0"
                            }`}
                            aria-label="Next slide"
                        >
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function Loading() {
    return (
        <div className="py-12 text-center">
            <Spinner />
        </div>
    );
}

function Error({ message }: { message: string }) {
    return (
        <div className="py-12 text-center">
            <Warning size={50} />
            <div className="mt-4">{message}</div>
        </div>
    );
}
